import { DURATION_UNIT, isTimeRangeInputValueAbsolute } from '@chronosphereio/core';
import { useEffect, useState } from 'react';
import { useTimeRange } from './TimeRangeProviderV2';

export type UseTimeRangeLastLoaded = {
  /**
   * If the last loaded message is enabled.
   */
  enabled: boolean;
  /**
   * The number of milliseconds since the last fetched end time.
   */
  lastLoadedMs?: number;
  /**
   * A string-based summary of the elapsed time since the relative time range was last updated.
   */
  lastLoadedMessage?: string;
};

export interface UseTimeRangeLastLoadedProps {
  /**
   * Use this property to disable the hook's logic. This may be useful if you are controlling the
   * usage of this hook by a feature flag or some other conditional logic.
   */
  disabled?: boolean;
  /**
   * Interval (in milliseconds) used to update the last loaded state.
   *
   * @default 15000
   */
  updateIntervalMs?: number;
}

/**
 * Minimum refresh interval (exclusive) that should trigger showing the "last loaded" message.
 * We do not show it for very small refresh intervals because they update often enough that it
 * seems unnecessary.
 */
const TIME_RANGE_LAST_LOADED_MIN_REFRESH_MS = 1 * 60 * 1000;

/**
 * Hook that provides information about the duration of time since the time range was last loaded when the time
 * range is relative and the refresh interval is off or greater than 1 minute.
 */
export function useTimeRangeLastLoaded({
  disabled,
  // This default was decided on as as a balance to ensure the numbers are relatively up-to-date without
  // running this constantly. We only show minute and greater fidelity, so 5s seemed reasonable.
  updateIntervalMs = 15 * 1000,
}: UseTimeRangeLastLoadedProps = {}): UseTimeRangeLastLoaded {
  const [lastLoadedMs, setLastLoadedMs] = useState<number | undefined>();
  const [lastLoadedMessage, setLastLoadedMessage] = useState<string | undefined>();

  const { timeRange, absoluteTimeRange, refreshIntervalInMs } = useTimeRange();

  // Enabled/disabled state needs to be near-instantaneous and is relatively cheap to calculate, so is
  // set in the render cycle (unlike the last loaded duration, which is calculated on an interval).
  const isEnabledForRefresh = !refreshIntervalInMs || refreshIntervalInMs > TIME_RANGE_LAST_LOADED_MIN_REFRESH_MS;
  const isEnabled = !disabled && isEnabledForRefresh && !isTimeRangeInputValueAbsolute(timeRange);

  useEffect(() => {
    function updateLastLoaded() {
      const newLastLoaded = Date.now() - absoluteTimeRange.end.getTime();
      setLastLoadedMs(newLastLoaded);
      setLastLoadedMessage(getLastLoadedMessage(newLastLoaded));
    }
    let intervalId: ReturnType<typeof setInterval>;

    if (isEnabled) {
      updateLastLoaded();

      // Regularly update the last loaded value over time to ensure it's up-to-date.
      intervalId = setInterval(updateLastLoaded, updateIntervalMs);
    } else {
      // Last loaded is not valid for the current status. Set to undefined and do not run an update
      // on an interval.
      setLastLoadedMs(undefined);
      setLastLoadedMessage(undefined);
    }

    return () => {
      // Ensure we clean up the setInterval when the useEffect changes or the hook unmounts.
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [absoluteTimeRange.end, isEnabled, updateIntervalMs]);

  // This intentionally treats the `lastLoadedMs` as falsy when `0` because it doesn't make sense to
  // show the last loaded information at that point. In practice, this case will be hit very rarely
  // in client-side code because this processing doesn't happen instantaneously.
  if (isEnabled && lastLoadedMs && lastLoadedMessage) {
    return {
      enabled: true,
      lastLoadedMs,
      lastLoadedMessage,
    };
  }

  return {
    enabled: false,
  };
}

type LastLoadedMessageOption = {
  /**
   * Label that will be shown after the number to indicate the time unit.
   */
  unitLabel: string;
  /**
   * Minimum duration (inclusive) that should be used with this config.
   */
  minDurationMs: number;
  /**
   * Optional prefix that will be shown before the number.
   */
  prefix?: string;
};

/**
 * List of potential message options that will be iterated to find the best match
 * when generating the last loaded message.
 *
 * These configurations MUST be kept with the `minDurationMs` in decreasing order for the
 * logic to work properly.
 */
const lastLoadedMessageOptions: LastLoadedMessageOption[] = [
  {
    unitLabel: DURATION_UNIT.DAY,
    minDurationMs: 24 * 60 * 60 * 1000,
  },
  {
    unitLabel: DURATION_UNIT.HOUR,
    minDurationMs: 60 * 60 * 1000,
  },
  {
    unitLabel: DURATION_UNIT.MINUTE,
    minDurationMs: 60 * 1000,
  },
  {
    prefix: '<',
    unitLabel: DURATION_UNIT.MINUTE,
    // 0 value is used to trigger slightly different behavior for the last use case where we show
    // <1m.
    minDurationMs: 0,
  },
];

/**
 * Takes the number of milliseconds since the relative time was last loaded and
 * translates it to a user-friendly message.
 */
const getLastLoadedMessage = (lastLoadedMs: number) => {
  const matchingOpt = lastLoadedMessageOptions.find((opt) => {
    return lastLoadedMs >= opt.minDurationMs;
  });

  if (!matchingOpt) {
    return '';
  }

  const formattedNumber = matchingOpt.minDurationMs ? Math.floor(lastLoadedMs / matchingOpt.minDurationMs) : 1;

  return `Loaded ${matchingOpt?.prefix || ''}${formattedNumber}${matchingOpt?.unitLabel} ago`;
};
